import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ApplicationRef, Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import {
  ApiError,
  APP_ROLE,
  AuthUser,
  ChangePassword,
  CreateUser,
  Login,
  ORGANISATION_CATEGORY,
  RequiredActionAlias,
  RoutePath,
  Setting,
  TokenResponse,
  User,
  USER_TYPE,
  VerifyEmail,
} from '@univacity/shared/types';
import { ObjectUtil } from '@univacity/shared/util';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Configuration } from '../config';
import { TRACKED_EVENTS } from '../interfaces';
import { AnalyticsService } from './analytics.service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public rememberMe = false;
  private baseUrl!: string;
  public user?: User;
  user$ = new BehaviorSubject<AuthUser | null>(null);
  get isAgencyUser(): boolean {
    return this.user?.organisationType === ORGANISATION_CATEGORY.agency;
  }
  get isInstituteUser(): boolean {
    return this.user?.organisationType === ORGANISATION_CATEGORY.provider;
  }
  get isOrganisationUser(): boolean {
    return !!this.user?.organisation;
  }

  get isStudent() {
    return this.user?.type === USER_TYPE.student || !this.user?.type;
  }

  get isAdmin() {
    return this.user?.type === USER_TYPE.admin;
  }

  constructor(
    private http: HttpClient,
    private router: Router,
    private config: Configuration,
    private storageService: StorageService,
    private analytics: AnalyticsService,
    private appRef: ApplicationRef,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    this.baseUrl = this.config.apiMainUrl + '/auth';
    if (isPlatformBrowser(this.platformId)) {
      this.user = JSON.parse(
        this.storageService.getItem(Setting.user) || '{}'
      ) as User;
      this.refreshUserProfile().subscribe((user) => {
        this.saveUser(user);
        this.appRef.tick();
      });
    }
  }

  refreshUserProfile() {
    return this.http.get<User>(`${this.baseUrl}/user/profile`, {
      params: { lean: false, populate: ['organisationType'] },
    });
  }

  get accessToken() {
    return this.storageService.getItem(Setting.accessToken);
  }

  get refreshToken() {
    return this.storageService.getItem(Setting.refreshToken);
  }

  hasRoles(roles: APP_ROLE[]) {
    return this.user && roles.includes(this.user.role);
  }

  public signIn(
    login: Login & { returnUrl?: string },
    recaptcha?: string
  ): Observable<AuthUser> {
    return this.http
      .post<TokenResponse>(
        `${this.baseUrl}/login`,
        {
          ...login,
        },
        {
          headers: ObjectUtil.removeEmptyProperties({
            recaptcha: recaptcha || '',
          }),
        }
      )
      .pipe(
        map(({ access_token, refresh_token }: TokenResponse) => {
          const user = jwtDecode(access_token) as AuthUser;
          this.analytics.track(TRACKED_EVENTS.signedIn, {
            email: user.email,
            userId: user._id,
            username: user.username,
            firstName: user.firstName,
            lastName: user.lastName,
            organisation: user.organisation,
          });
          this.saveUser(user, refresh_token, access_token);

          if (login.returnUrl) this.router.navigateByUrl(login.returnUrl);
          else window.location.reload();
          return user;
        }),

        catchError(async (err) => {
          console.error({ err });
          if (err.statusCode == 400) {
            const requiredActions: RequiredActionAlias[] =
              err.response.requiredActions || [];
            if (requiredActions.includes(RequiredActionAlias.UPDATE_PASSWORD)) {
              await this.router.navigate([RoutePath.authResetPassword], {
                queryParams: {
                  email: login.email,
                  code: err.response.changePasswordCode,
                },
              });
            } else if (
              requiredActions.includes(RequiredActionAlias.VERIFY_EMAIL)
            ) {
              err.message = $localize`You need to very your email address. Please check your email for instruction`;
              await this.router.navigate([RoutePath.authEmailVerification], {
                queryParams: { email: login.email },
              });
            }
          }
          throw err;
        })
      );
  }

  private saveUser(
    user: AuthUser,
    refreshToken?: string,
    accessToken?: string
  ) {
    if (refreshToken)
      this.storageService.setItem(Setting.refreshToken, refreshToken);
    if (accessToken)
      this.storageService.setItem(Setting.accessToken, accessToken);
    const stringifiedUser = JSON.stringify(user);
    this.storageService.setItem(Setting.user, stringifiedUser);
    this.user = user;
    this.analytics.identify(user);
    this.user$.next(user);
  }

  public get isLoggedIn(): boolean {
    return !!this.user?._id;
    // try {
    //   const { exp } = jwt_decode(this.refreshToken || '') as { exp: number };
    //   return exp > Date.now() / 1000;
    // } catch (e) {
    //   return false;
    // }
  }

  public refreshOfflineToken(): Observable<TokenResponse> {
    const reqBody = { refreshToken: this.refreshToken };
    return this.http
      .post<TokenResponse>(`${this.baseUrl}/refresh/token`, reqBody)
      .pipe(
        tap(({ refresh_token, access_token }) => {
          const user = jwtDecode(access_token) as AuthUser;
          this.saveUser(user, refresh_token);
        }),
        catchError((authError: ApiError) => {
          console.error({ authError });
          this.logout();
          throw authError;
        })
      );
  }

  public logout(): void {
    this.storageService.removeItem(Setting.user);
    this.storageService.removeItem(Setting.accessToken);
    this.storageService.removeItem(Setting.refreshToken);
    this.analytics.reset();
    this.http.get(`${this.baseUrl}/logout`).subscribe(() => {
      window.location.reload();
      this.user$.next(null);
      this.user = undefined;
    });
  }

  signUp(
    user: CreateUser & { returnUrl?: string; password: string },
    recaptcha: string
  ) {
    return this.http
      .post(
        `${this.baseUrl}/register`,
        ObjectUtil.removeEmptyProperties(user),
        { headers: { recaptcha } }
      )
      .pipe(
        tap(() => {
          this.analytics.track(TRACKED_EVENTS.signUp, {
            email: user.email,
            username: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
          });
        }),
        switchMap(() =>
          this.signIn({
            email: user.email,
            password: user.password,
            returnUrl: user.returnUrl,
          })
        )
      );
  }

  sendEmailVerification(username: string) {
    return this.http.post(`${this.baseUrl}/send/email-verification`, {
      username,
    });
  }
  verifyEmail(body: VerifyEmail) {
    return this.http.post(`${this.baseUrl}/email-verify`, body);
  }
  changePassword(body: ChangePassword) {
    return this.http
      .post(`${this.baseUrl}/change-password`, body)
      .pipe(switchMap(() => this.signIn(body)));
  }

  forgotPassword(data: { email: string }) {
    return this.http.post(`${this.baseUrl}/reset-password`, data);
  }
  trackReferrer(refCode: string) {
    return this.http.get(`${this.baseUrl}/track-referral/${refCode}`);
  }
}
